import React from 'react';
import Layout from '@/components/Layout';
import { useQuery } from '@tanstack/react-query';
import contentfulService from '@/services/contentfulService';
import { Skeleton } from '@/components/ui/skeleton';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

// Import refactored components
import MagazineHero from '@/components/index/MagazineHero';
import LatestStoriesSection from '@/components/index/LatestStoriesSection';
import HomePlaylistsCarousel from '@/components/index/HomePlaylistsCarousel';
import FeaturedVideoSection from '@/components/index/FeaturedVideoSection';
import CategoryStoriesSection from '@/components/index/CategoryStoriesSection';
import FeaturedVideosSection from '@/components/index/FeaturedVideosSection';
import YouTubeShortsSection from '@/components/index/YouTubeShortsSection';
import HomepageYouTubeHero from '@/components/index/HomepageYouTubeHero';
import EditorsPicksCarousel from '@/components/index/EditorsPicksCarousel';

const Index = () => {
  const { resolvedTheme } = useTheme();
  
  const {
    data: stories = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['stories'],
    queryFn: contentfulService.getStories
  });

  const storyCategories = Array.from(new Set(stories.map(story => story.category))).map(category => ({
    name: category,
    stories: stories.filter(story => story.category === category)
  }));

  const featuredStories = stories.filter(story => story.featured).slice(0, 1);
  const heroStoryId = featuredStories.length > 0 ? featuredStories[0].id : '';
  
  // Get all stories except the hero story for diverse display
  const availableStories = stories.filter(story => story.id !== heroStoryId);

  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';

  if (isLoading) {
    return (
      <Layout>
        <div className="article-container py-8">
          <Skeleton className="h-96 w-full mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} className="h-80 w-full" />)}
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="article-container py-16 text-center">
          <h2 className={cn("text-2xl mb-4", textColor)}>Error loading content</h2>
          <p className={cn("mb-4", mutedTextColor)}>
            There was a problem connecting to the content service. Please try again later.
          </p>
        </div>
      </Layout>
    );
  }

  // Find Architecture category index
  const architectureIndex = storyCategories.findIndex(
    category => category.name.toLowerCase() === 'architecture'
  );

  return (
    <Layout>
      {/* Magazine Hero Section */}
      <MagazineHero stories={stories} />

      {/* Stories from Various Categories Section */}
      <LatestStoriesSection stories={availableStories} />

      {/* YouTube Hero Video */}
      <HomepageYouTubeHero 
        videoId="izEvvjRy5R4" 
        title="Featured Video"
      />

      {/* Featured Videos Section */}
      <FeaturedVideosSection />

      {/* YouTube Shorts Section */}
      <YouTubeShortsSection />

      {/* Playlists Section */}
      <HomePlaylistsCarousel />

      {/* Editor's Picks Carousel */}
      <EditorsPicksCarousel stories={stories} />

      {/* Architecture Category (if found) */}
      {architectureIndex >= 0 && (
        <CategoryStoriesSection 
          key={storyCategories[architectureIndex].name} 
          category={storyCategories[architectureIndex]} 
        />
      )}

      {/* Other Category Stories Sections - Skip Architecture as it's already displayed */}
      {storyCategories
        .filter((_, index) => index !== architectureIndex)
        .slice(0, 2)
        .map((category) => (
          <CategoryStoriesSection key={category.name} category={category} />
        ))
      }

    </Layout>
  );
};

export default Index;
